<template>
  <base-page-template>
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        ユーザ管理
      </v-toolbar-title>
    </template>

    <owner-info
        :account="account">
    </owner-info>

    <tab-item-template>
      <template #toolbar-items>
        <el-tooltip content="新規ユーザ追加" placement="bottom">
          <v-btn icon @click="openCreateDialog">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </el-tooltip>
        <v-spacer></v-spacer>
        <model-select
            v-model="query.terminalIdEq"
            :items="terminals"
            placeholder="サイト"
            @change="findUsers"
            clearable
            outlined
            dense
            hide-details
            light
            background-color="white"
            :style="{maxWidth: '300px'}">
        </model-select>
      </template>

      <el-table
          :data="usersView"
          :row-class-name="({row}) => row.invalidFlag ? 'deactivated' : ''"
          border>
        <el-table-column
            prop="email"
            label="メールアドレス"
            sortable>
        </el-table-column>

        <el-table-column
            class-name="name"
            prop="name"
            label="氏名"
            sortable>
        </el-table-column>

        <el-table-column
            prop="terminal.name"
            label="サイト"
            sortable>
          <template #default="{row}">
            {{ row.terminal.name }}
            <el-popover
                v-if="row.availableTerminals.length > 1"
                placement="right"
                width="300"
                :open-delay="200"
                trigger="hover">
          <span class="omitted-terminals" slot="reference">
            {{ `+${row.availableTerminals.length - 1}` }}
          </span>
              <template #default>
                <header class="available-sites">以下のサイトが利用可能です</header>
                <ul class="available-sites">
                  <li v-for="at in row.availableTerminals" :key="at.id">{{ at.name }}</li>
                </ul>
              </template>
            </el-popover>
          </template>
        </el-table-column>

        <el-table-column
            prop="permissionAdmin"
            label="管理者"
            width="70"
            align="center">
          <template #default="{row}">
            <readonly-checkbox :value="row.permissionAdmin"></readonly-checkbox>
          </template>
        </el-table-column>

        <el-table-column
            prop="permissionImplementation"
            label="実装"
            width="60"
            align="center">
          <template #default="{row}">
            <readonly-checkbox :value="row.permissionImplementation"></readonly-checkbox>
          </template>
        </el-table-column>

        <el-table-column
            prop="permissionContract"
            label="取引"
            width="60"
            align="center">
          <template #default="{row}">
            <readonly-checkbox :value="row.permissionContract"></readonly-checkbox>
          </template>
        </el-table-column>

        <el-table-column
            prop="permissionDictionary"
            label="辞書"
            width="60"
            align="center">
          <template #default="{row}">
            <readonly-checkbox :value="row.permissionDictionary"></readonly-checkbox>
          </template>
        </el-table-column>

        <el-table-column
            prop="description"
            label="備考">
          <template #default="{row}">
            <table-inline>
              <text-field-like
                  :value="row.description"
                  placeholder="未設定"
                  multi-line>
              </text-field-like>
            </table-inline>
          </template>
        </el-table-column>

        <el-table-column
            prop="registeredAt"
            label="登録日"
            width="100"
            sortable>
          <template #default="{row}">{{ formatDate(row.registeredAt) }}</template>
        </el-table-column>

        <el-table-column
            width="95"
            align="center"
            fixed="right">
          <template #default="{row}">
            <icon-btns>
              <icon-btn
                  type="delete"
                  @click="openDeleteDialog(row)"
                  :disabled="row.lastAdmin">
              </icon-btn>
              <icon-btn
                  type="edit-content"
                  @click="openUpdateDialog(row)">
              </icon-btn>
            </icon-btns>
          </template>
        </el-table-column>
      </el-table>
    </tab-item-template>

    <!-- 作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreate"
        title-text="ユーザ作成"
        @close="closeCreateDialog"
        @apply="createUser">
      <user-form
          v-model="userForm"
          is-current-label="初回ログイン">
      </user-form>
    </simple-dialog>

    <!-- 更新ダイアログ -->
    <simple-dialog
        v-model="dialogUpdate"
        title-text="ユーザ編集"
        @close="closeUpdateDialog"
        @apply="updateUser">
      <user-form
          v-model="userForm">
      </user-form>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete"
        title-text="ユーザ削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のユーザを削除します。本当によろしいですか？"
        @close="closeDeleteDialog"
        @apply="deleteUser">
      <user-form
          v-model="userForm"
          readonly>
      </user-form>
    </simple-dialog>
  </base-page-template>
</template>

<script>
import {mapState, mapMutations, mapGetters} from "vuex";
import IoablePageMixin from "@/router/IoablePageMixin";
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import UserForm from "@/components/pages/admin/UserList/UserForm.vue";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import OwnerInfo from "@/components/organisms/OwnerInfo.vue";
import IconBtn from "@/components/molecules/IconBtn.vue";
import IconBtns from "@/components/molecules/IconBtns";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import Formatter from "@/utils/Formatter";
import Swal from "sweetalert2";
import TableInline from "@/components/atoms/TableInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";

export default {
  name: 'UserList',
  components: {
    TextFieldLike,
    TableInline,
    ReadonlyCheckbox,
    IconBtns,
    IconBtn,
    ModelSelect,
    TabItemTemplate,
    BasePageTemplate,
    SimpleDialog,
    OwnerInfo,
    UserForm
  },
  mixins: [
    IoablePageMixin,
    Formatter,
  ],
  data() {
    return {
      dialogCreate: false,
      dialogUpdate: false,
      dialogDelete: false,
      userForm: {},
      query: {
        terminalIdEq: null
      }
    }
  },
  computed: {
    ...mapState('hcs/user', ['users']),
    ...mapState('hcs/terminal', ['terminals']),
    ...mapGetters({
      account: 'hcs/account'
    }),
    usersView() {
      const admins = this.users.filter(user => !user.invalidFlag && user.permissionAdmin);
      const lastAdminId = admins.length === 1 ? admins[0].id : null;
      return this.users.map(user => {
        return {
          ...user,
          lastAdmin: lastAdminId === user.id,
        };
      });
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
      setLoginUser: 'hcs/setLoginUser',
    }),
    load() {
      return Promise.all([
        this.findUsers(),
        this.$store.dispatch('hcs/terminal/findList')
      ])
    },
    findUsers() {
      return this.$store.dispatch('hcs/user/findList', this.query)
    },
    openCreateDialog() {
      this.userForm = this.defaultForm();
      this.dialogCreate = true;
    },
    async closeCreateDialog() {
      this.dialogCreate = false;
    },
    openUpdateDialog(user) {
      this.userForm = {
        ...user,
        availableTerminalIds: user.availableTerminals.map(t => t.id)
      };
      delete this.userForm.owner;
      delete this.userForm.terminal;
      delete this.userForm.availableTerminals;
      this.dialogUpdate = true;
    },
    closeUpdateDialog() {
      this.dialogUpdate = false;
    },
    openDeleteDialog(user) {
      this.userForm = {
        ...user,
        availableTerminalIds: user.availableTerminals.map(t => t.id)
      };
      delete this.userForm.owner;
      delete this.userForm.terminal;
      delete this.userForm.availableTerminals;
      this.dialogDelete = true;
    },
    closeDeleteDialog() {
      this.dialogDelete = false;
    },
    defaultForm() {
      return {
        terminalId: '',
        availableTerminalIds: [],
        email: '',
        name: '',
        invalidFlag: false,
        permissionAdmin: true,
        permissionImplementation: true,
        permissionContract: true,
        permissionDictionary: true
      }
    },
    async createUser() {
      this.showSnackbar({
        message: 'ユーザを作成中...',
        timeout: false,
        loading: true
      });
      try {
        await this.$store.dispatch('hcs/user/create', this.userForm);

        this.$store.commit({
          type: 'showSnackbar',
          timeout: 30000,
          message: `ユーザーの作成に成功しました。該当のメールアドレスに案内が送信されました。`
        });
      } catch (e) {
        console.error(e);
        this.$store.commit({
          type: 'showSnackbar',
          color: 'failed',
          message: `ユーザーの作成に失敗しました`
        });
      }
    },
    updateUser() {
      this.showSnackbar({
        message: 'ユーザを保存中...',
        timeout: false,
        loading: true
      });
      this.$store.dispatch('hcs/user/update', this.userForm)
        .then(user => {
          if(user.id === this.account.user?.id) {
            this.setLoginUser({loginUser: user});
          }
          this.$store.commit({
            type: 'showSnackbar',
            message: `保存に成功しました`
          });
        })
        .catch(reason => {
          console.error(reason);
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `保存に失敗しました`
          });
        })
    },
    deleteUser() {
      this.showSnackbar({
        message: 'ユーザを削除中...',
        timeout: false,
        loading: true
      });
      this.$store.dispatch('hcs/user/delete', this.userForm.id)
        .then(() => {
          this.$store.commit({
            type: 'showSnackbar',
            message: `ユーザが削除されました`
          });
        })
        .catch(reason => {
          console.error(reason);
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `ユーザの削除に失敗しました`
          });
        })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep tr.deactivated td.name {
  text-decoration: line-through;
}

.omitted-terminals {
  border-radius: 5px;
  padding: 0 0.3rem;
  margin-left: 0.5rem;
  display: inline-block;
  font-size: 90%;
  font-weight: bold;
  font-family: monospace;
  background-color: #616161;
  color: white;
  cursor: default;
}

header.available-sites {
  font-size: 90%;
  margin-bottom: .3rem;
}

ul.available-sites {
  list-style-position: inside;
  font-size: 11pt;
}
</style>
